import { CustomLayerInterface, CustomRenderMethodInput, Map as MlMap } from "maplibre-gl";
import { lngLatElevToMercator } from "./webgl-maplibre.obstacle-3d-layer";
import { ProcedureGeometry, ProcedureLabel } from "./webgl-maplibre.procedure-geometry";

// Line quads, as in the obstacle layer: 6 vertices per segment, each carrying
// both endpoints and a side (±1 at the start, ±2 at the end). Every vertex also
// carries its segment's style and its ground distance along the line, so one
// draw call holds every width, colour and dash pattern.
const LINE_STRIDE = 15; // cur xyz, other xyz, side, rgba, half width, dash on, dash off, distance
const LINE_VERT_SRC = `
attribute vec3 a_pos_cur;
attribute vec3 a_pos_oth;
attribute float a_side;
attribute vec4 a_color;
attribute vec3 a_style;
attribute float a_distance;
uniform mat4 u_matrix;
uniform vec2 u_viewport;
uniform float u_dpr;
varying float v_dist_w;
varying float v_w;
varying float v_half_width;
varying vec4 v_color;
varying vec2 v_dash;
varying float v_distance;
void main() {
  v_color = a_color;
  v_half_width = a_style.x * u_dpr;
  v_dash = a_style.yz * v_half_width * 2.0;
  v_distance = a_distance;
  vec4 cur = u_matrix * vec4(a_pos_cur, 1.0);
  vec4 oth = u_matrix * vec4(a_pos_oth, 1.0);
  if (cur.w <= 0.0 || oth.w <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vec2 d = oth.xy / oth.w - cur.xy / cur.w;
  float len = max(length(d * u_viewport), 1e-3);
  float endSign = abs(a_side) > 1.5 ? -1.0 : 1.0;
  vec2 dir = endSign * (d * u_viewport) / len;
  vec2 nrm = vec2(-dir.y, dir.x);
  float dist = sign(a_side) * (v_half_width + 1.0);
  v_dist_w = dist * cur.w;
  v_w = cur.w;
  gl_Position = vec4(cur.xy + (nrm * dist / u_viewport) * 2.0 * cur.w, cur.z, cur.w);
}
`;

// Dashes measure the ground distance in CSS pixels at the view centre, like
// MapLibre's dash arrays, which are in line widths at the current zoom.
const LINE_FRAG_SRC = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform float u_px_per_m;
varying float v_dist_w;
varying float v_w;
varying float v_half_width;
varying vec4 v_color;
varying vec2 v_dash;
varying float v_distance;
void main() {
  if (v_dash.x > 0.0) {
    float period = v_dash.x + v_dash.y;
    if (mod(v_distance * u_px_per_m, period) > v_dash.x) discard;
  }
  float a = clamp(v_half_width + 0.5 - abs(v_dist_w / v_w), 0.0, 1.0) * v_color.a;
  gl_FragColor = vec4(v_color.rgb * a, a);
}
`;

// Round joins and caps for solid lines: one smoothed disc per vertex.
const JOIN_VERT_SRC = `
attribute vec3 a_pos;
attribute vec4 a_color;
attribute float a_half_width;
uniform mat4 u_matrix;
uniform float u_dpr;
varying vec4 v_color;
varying float v_half_width;
void main() {
  v_color = a_color;
  v_half_width = a_half_width * u_dpr;
  vec4 c = u_matrix * vec4(a_pos, 1.0);
  if (c.w <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    return;
  }
  gl_Position = c;
  gl_PointSize = 2.0 * (v_half_width + 1.0);
}
`;

const JOIN_FRAG_SRC = `
precision mediump float;
varying vec4 v_color;
varying float v_half_width;
void main() {
  float d = length(gl_PointCoord - 0.5) * 2.0 * (v_half_width + 1.0);
  float a = clamp(v_half_width + 0.5 - d, 0.0, 1.0) * v_color.a;
  gl_FragColor = vec4(v_color.rgb * a, a);
}
`;

const FILL_VERT_SRC = `
attribute vec3 a_pos;
attribute vec4 a_color;
uniform mat4 u_matrix;
varying vec4 v_color;
void main() {
  v_color = a_color;
  vec4 c = u_matrix * vec4(a_pos, 1.0);
  gl_Position = c.w <= 0.0 ? vec4(2.0, 2.0, 2.0, 1.0) : c;
}
`;

const FILL_FRAG_SRC = `
precision mediump float;
varying vec4 v_color;
void main() {
  gl_FragColor = vec4(v_color.rgb * v_color.a, v_color.a);
}
`;

function program(gl: WebGLRenderingContext, vertex: string, fragment: string): WebGLProgram {
  const p = gl.createProgram();
  for (const [type, src] of [
    [gl.VERTEX_SHADER, vertex],
    [gl.FRAGMENT_SHADER, fragment],
  ] as const) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(`procedure shader: ${gl.getShaderInfoLog(s)}`);
    gl.attachShader(p, s);
  }
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(`procedure program: ${gl.getProgramInfoLog(p)}`);
  return p;
}

interface Pass {
  program: WebGLProgram;
  buffer: WebGLBuffer;
  count: number;
  /** [attribute name, size] in buffer order; the stride is their sum. */
  attributes: [string, number][];
}

/**
 * Approach procedures on flat sheets at their airport elevations, drawn in
 * the main map. Each procedure plane used to be its own MapLibre map with its
 * own WebGL context, and the GPU switching between up to six contexts cost
 * more than the drawing. Path labels are HTML, like the route labels: a custom
 * layer cannot use MapLibre's glyphs.
 */
export class ProcedureLayer implements CustomLayerInterface {
  readonly id = "procedure-3d";
  readonly type = "custom" as const;
  readonly renderingMode = "3d" as const;

  private map?: MlMap;
  private gl: WebGLRenderingContext | null = null;
  private passes: { fill?: Pass; line?: Pass; join?: Pass } = {};
  private geometry: ProcedureGeometry = {
    lines: new Float64Array(0),
    lineStyle: new Float32Array(0),
    lineDistance: new Float64Array(0),
    fills: new Float64Array(0),
  };
  // Vertex data is relative to this mercator origin, as in the obstacle layer:
  // float32 world coordinates round to about 2 m.
  private originX = 0;
  private originY = 0;
  private readonly rtc = new Float32Array(16);
  private labels: { element: HTMLDivElement; anchor: number[]; toward: number[]; width: number; shown: string }[] = [];
  private measure = document.createElement("canvas").getContext("2d");

  setData(geometry: ProcedureGeometry, labels: ProcedureLabel[]): void {
    this.geometry = geometry;
    const first = geometry.lines.length ? geometry.lines : geometry.fills;
    this.originX = first[0] ?? 0;
    this.originY = first[1] ?? 0;
    this.upload();
    this.setLabels(labels);
  }

  onAdd(map: MlMap, gl: WebGLRenderingContext): void {
    this.map = map;
    this.gl = gl;
    const pass = (vertex: string, fragment: string, attributes: [string, number][]): Pass => ({
      program: program(gl, vertex, fragment),
      buffer: gl.createBuffer(),
      count: 0,
      attributes,
    });
    this.passes = {
      fill: pass(FILL_VERT_SRC, FILL_FRAG_SRC, [
        ["a_pos", 3],
        ["a_color", 4],
      ]),
      line: pass(LINE_VERT_SRC, LINE_FRAG_SRC, [
        ["a_pos_cur", 3],
        ["a_pos_oth", 3],
        ["a_side", 1],
        ["a_color", 4],
        ["a_style", 3],
        ["a_distance", 1],
      ]),
      join: pass(JOIN_VERT_SRC, JOIN_FRAG_SRC, [
        ["a_pos", 3],
        ["a_color", 4],
        ["a_half_width", 1],
      ]),
    };
    this.upload();
    this.setLabels(this.pendingLabels);
  }

  onRemove(): void {
    const gl = this.gl;
    for (const pass of Object.values(this.passes)) {
      gl?.deleteProgram(pass.program);
      gl?.deleteBuffer(pass.buffer);
    }
    this.passes = {};
    this.gl = null;
    this.setLabels([]);
  }

  private upload(): void {
    const gl = this.gl;
    const { fill, line, join } = this.passes;
    if (!gl || !fill || !line || !join) return;
    const { lines, lineStyle, lineDistance, fills } = this.geometry;
    const ox = this.originX,
      oy = this.originY;

    const fillData = new Float32Array(fills.length);
    for (let i = 0; i < fills.length; i += 7) {
      fillData.set([fills[i] - ox, fills[i + 1] - oy, fills[i + 2]], i);
      fillData.set(fills.subarray(i + 3, i + 7), i + 3);
    }

    const segments = lines.length / 6;
    const lineData = new Float32Array(segments * 6 * LINE_STRIDE);
    const joinData: number[] = [];
    let o = 0;
    for (let s = 0; s < segments; s++) {
      const a = [lines[s * 6] - ox, lines[s * 6 + 1] - oy, lines[s * 6 + 2]];
      const b = [lines[s * 6 + 3] - ox, lines[s * 6 + 4] - oy, lines[s * 6 + 5]];
      const style = lineStyle.subarray(s * 7, s * 7 + 7);
      const [da, db] = [lineDistance[s * 2], lineDistance[s * 2 + 1]];
      for (const [cur, oth, side, distance] of [
        [a, b, -1, da],
        [a, b, 1, da],
        [b, a, -2, db],
        [a, b, 1, da],
        [b, a, 2, db],
        [b, a, -2, db],
      ] as const) {
        lineData.set(cur, o);
        lineData.set(oth, o + 3);
        lineData[o + 6] = side;
        lineData.set(style, o + 7);
        lineData[o + 14] = distance;
        o += LINE_STRIDE;
      }
      // ponytail: dashed lines get square dash ends; MapLibre rounds them.
      if (style[5] > 0) continue;
      joinData.push(...a, ...style.subarray(0, 5));
      const next = s + 1 < segments && lineDistance[(s + 1) * 2] !== 0;
      if (!next) joinData.push(...b, ...style.subarray(0, 5));
    }

    for (const [pass, data] of [
      [fill, fillData],
      [line, lineData],
      [join, new Float32Array(joinData)],
    ] as const) {
      gl.bindBuffer(gl.ARRAY_BUFFER, pass.buffer);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      pass.count = data.length / pass.attributes.reduce((sum, [, size]) => sum + size, 0);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    this.map?.triggerRepaint();
  }

  private pendingLabels: ProcedureLabel[] = [];
  private setLabels(labels: ProcedureLabel[]): void {
    this.pendingLabels = labels;
    for (const label of this.labels) label.element.remove();
    this.labels = [];
    const container = this.map?.getCanvasContainer();
    if (!container || !this.gl) return;
    for (const label of labels) {
      const element = document.createElement("div");
      element.textContent = label.text;
      const font = `${label.bold ? 800 : 600} 22px 'Open Sans', sans-serif`;
      if (this.measure) this.measure.font = font;
      // text-size 22 and text-offset [0, -0.8] of vm-procedure-path-labels.
      Object.assign(element.style, {
        position: "absolute",
        left: "0",
        top: "0",
        display: "none",
        pointerEvents: "none",
        whiteSpace: "nowrap",
        color: label.color,
        font,
        textShadow: "0 0 1px #fff, 0 0 1px #fff",
      });
      container.appendChild(element);
      this.labels.push({
        element,
        anchor: lngLatElevToMercator(label.lng, label.lat, label.elevation),
        toward: lngLatElevToMercator(label.toward[0], label.toward[1], label.elevation),
        width: this.measure?.measureText(label.text).width ?? label.text.length * 12,
        shown: "none",
      });
    }
  }

  private rtcMatrix(matrix: ArrayLike<number>): Float32Array {
    for (let i = 0; i < 12; i++) this.rtc[i] = matrix[i];
    for (let r = 0; r < 4; r++)
      this.rtc[12 + r] = matrix[r] * this.originX + matrix[4 + r] * this.originY + matrix[12 + r];
    return this.rtc;
  }

  render(gl: WebGLRenderingContext | WebGL2RenderingContext, input: CustomRenderMethodInput): void {
    const main = input.defaultProjectionData.mainMatrix as unknown as ArrayLike<number>;
    this.placeLabels(main);
    const { fill, line, join } = this.passes;
    if (!fill || !line || !join || fill.count + line.count === 0) return;
    const map = this.map;
    const matrix = this.rtcMatrix(main);
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    // CSS pixels per ground metre at the view centre (512 px tiles).
    const lat = (map.getCenter().lat * Math.PI) / 180;
    const pxPerM = (512 * 2 ** map.getZoom()) / (40075016.686 * Math.cos(lat));

    // Flat sheets, drawn over the terrain like the planes were: no depth test.
    // MapLibre sets its own state again after a custom layer.
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    for (const [pass, mode] of [
      [fill, gl.TRIANGLES],
      [line, gl.TRIANGLES],
      [join, gl.POINTS],
    ] as const) {
      if (!pass.count) continue;
      gl.useProgram(pass.program);
      gl.uniformMatrix4fv(gl.getUniformLocation(pass.program, "u_matrix"), false, matrix);
      gl.uniform2f(gl.getUniformLocation(pass.program, "u_viewport"), gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.uniform1f(gl.getUniformLocation(pass.program, "u_dpr"), dpr);
      gl.uniform1f(gl.getUniformLocation(pass.program, "u_px_per_m"), pxPerM * dpr);
      gl.bindBuffer(gl.ARRAY_BUFFER, pass.buffer);
      const stride = pass.attributes.reduce((sum, [, size]) => sum + size, 0) * 4;
      const enabled: number[] = [];
      let offset = 0;
      for (const [name, size] of pass.attributes) {
        const location = gl.getAttribLocation(pass.program, name);
        if (location >= 0) {
          gl.enableVertexAttribArray(location);
          gl.vertexAttribPointer(location, size, gl.FLOAT, false, stride, offset);
          enabled.push(location);
        }
        offset += size * 4;
      }
      gl.drawArrays(mode, 0, pass.count);
      for (const location of enabled) gl.disableVertexAttribArray(location);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }

  /** Screen position of each label, turned along its line and kept upright. */
  private placeLabels(m: ArrayLike<number>): void {
    if (!this.labels.length) return;
    const canvas = this.map.getCanvas();
    const w = canvas.clientWidth,
      h = canvas.clientHeight;
    const project = ([x, y, z]: number[]): [number, number] | null => {
      const cw = m[3] * x + m[7] * y + m[11] * z + m[15];
      if (cw <= 0) return null;
      return [
        (((m[0] * x + m[4] * y + m[8] * z + m[12]) / cw + 1) * w) / 2,
        ((1 - (m[1] * x + m[5] * y + m[9] * z + m[13]) / cw) * h) / 2,
      ];
    };
    // Like the old symbol layer's text-allow-overlap: false, a label that
    // overlaps one placed before it is hidden. Boxes are the screen-aligned
    // bounds of the turned text, 30 px tall, around where the text is drawn.
    const placed: number[][] = [];
    for (const label of this.labels) {
      const a = project(label.anchor);
      const b = project(label.toward);
      let shown = "none";
      if (a && b && a[0] >= -50 && a[0] <= w + 50 && a[1] >= -50 && a[1] <= h + 50) {
        let angle = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
        if (angle > 90) angle -= 180;
        else if (angle < -90) angle += 180;
        const rad = (angle * Math.PI) / 180;
        const cos = Math.abs(Math.cos(rad)),
          sin = Math.abs(Math.sin(rad));
        const hw = (label.width * cos + 30 * sin) / 2,
          hh = (label.width * sin + 30 * cos) / 2;
        // The transform below moves the text 17.6 px up in its own turned frame.
        const cx = a[0] + 17.6 * Math.sin(rad),
          cy = a[1] - 17.6 * Math.cos(rad);
        const box = [cx - hw, cy - hh, cx + hw, cy + hh];
        if (!placed.some((p) => box[0] < p[2] && p[0] < box[2] && box[1] < p[3] && p[1] < box[3])) {
          placed.push(box);
          shown = `translate(${Math.round(a[0])}px, ${Math.round(a[1])}px) rotate(${angle.toFixed(1)}deg) translate(-50%, calc(-50% - 17.6px))`;
        }
      }
      if (shown === label.shown) continue;
      if (shown === "none") label.element.style.display = "none";
      else {
        if (label.shown === "none") label.element.style.display = "block";
        label.element.style.transform = shown;
      }
      label.shown = shown;
    }
  }
}
